/**
 * Unified Asset Creation Component
 * Handles creation of all 8 asset types with appropriate forms and editors
 */

import React, { useState } from 'react';
import TipTapEditor from './asset-editors/TipTapEditor';
import {
  Upload, FileText, Image, Video, Globe, HelpCircle,
  Layout, Megaphone, Mic, Save, X, ChevronRight,
  Mail, Tag, Calendar, Users, Link, Download
} from 'lucide-react';
import axios from 'axios';

// Asset type definitions matching backend
type AssetType = 'email' | 'image' | 'video' | 'seo_content' | 'faq' | 'site_content' | 'ad' | 'podcast';

interface AssetMetadata {
  type: AssetType;
  title?: string;
  description?: string;
  owner: string;
  campaign?: string;
  tags: string[];
  target_audience?: string;
  [key: string]: any; // Allow type-specific fields
}

// Asset type selector component
function AssetTypeSelector({ value, onChange }: { value: AssetType; onChange: (type: AssetType) => void }) {
  const assetTypes = [
    { type: 'email' as AssetType, icon: Mail, label: 'Email', color: 'bg-blue-500' },
    { type: 'image' as AssetType, icon: Image, label: 'Image', color: 'bg-green-500' },
    { type: 'video' as AssetType, icon: Video, label: 'Video', color: 'bg-purple-500' },
    { type: 'seo_content' as AssetType, icon: Globe, label: 'SEO Content', color: 'bg-indigo-500' },
    { type: 'faq' as AssetType, icon: HelpCircle, label: 'FAQ', color: 'bg-yellow-500' },
    { type: 'site_content' as AssetType, icon: Layout, label: 'Site Content', color: 'bg-pink-500' },
    { type: 'ad' as AssetType, icon: Megaphone, label: 'Ad', color: 'bg-red-500' },
    { type: 'podcast' as AssetType, icon: Mic, label: 'Podcast', color: 'bg-gray-500' },
  ];

  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-3">Select Asset Type</label>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {assetTypes.map(({ type, icon: Icon, label, color }) => (
          <button
            key={type}
            onClick={() => onChange(type)}
            className={`p-4 rounded-lg border-2 transition-all ${
              value === type
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <div className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center mx-auto mb-2`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <p className="text-sm font-medium">{label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// Email metadata form
function EmailMetadataForm({ metadata, onChange }: { metadata: any; onChange: (data: any) => void }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line *</label>
          <input
            type="text"
            value={metadata.subject || ''}
            onChange={(e) => onChange({ ...metadata, subject: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter email subject"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Preheader</label>
          <input
            type="text"
            value={metadata.preheader || ''}
            onChange={(e) => onChange({ ...metadata, preheader: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Preview text"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From Name *</label>
          <input
            type="text"
            value={metadata.from_name || ''}
            onChange={(e) => onChange({ ...metadata, from_name: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From Email *</label>
          <input
            type="email"
            value={metadata.from_email || ''}
            onChange={(e) => onChange({ ...metadata, from_email: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Send Date</label>
        <input
          type="datetime-local"
          value={metadata.send_date || ''}
          onChange={(e) => onChange({ ...metadata, send_date: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="mobile-responsive"
          checked={metadata.mobile_responsive !== false}
          onChange={(e) => onChange({ ...metadata, mobile_responsive: e.target.checked })}
          className="mr-2"
        />
        <label htmlFor="mobile-responsive" className="text-sm text-gray-700">Mobile Responsive</label>
      </div>
    </div>
  );
}

// Image metadata form
function ImageMetadataForm({ metadata, onChange }: { metadata: any; onChange: (data: any) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text *</label>
        <input
          type="text"
          value={metadata.alt_text || ''}
          onChange={(e) => onChange({ ...metadata, alt_text: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Describe the image for accessibility"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
        <textarea
          value={metadata.caption || ''}
          onChange={(e) => onChange({ ...metadata, caption: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Optional image caption"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Photographer/Source</label>
          <input
            type="text"
            value={metadata.photographer || ''}
            onChange={(e) => onChange({ ...metadata, photographer: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Usage Rights</label>
          <select
            value={metadata.usage_rights || ''}
            onChange={(e) => onChange({ ...metadata, usage_rights: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            <option value="owned">Owned</option>
            <option value="licensed">Licensed</option>
            <option value="royalty_free">Royalty Free</option>
            <option value="creative_commons">Creative Commons</option>
          </select>
        </div>
      </div>
    </div>
  );
}

// Video metadata form
function VideoMetadataForm({ metadata, onChange }: { metadata: any; onChange: (data: any) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Video Title *</label>
        <input
          type="text"
          value={metadata.title || ''}
          onChange={(e) => onChange({ ...metadata, title: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={metadata.description || ''}
          onChange={(e) => onChange({ ...metadata, description: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={3}
        />
      </div>

      <div className="flex items-center space-x-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="captions"
            checked={metadata.captions_available || false}
            onChange={(e) => onChange({ ...metadata, captions_available: e.target.checked })}
            className="mr-2"
          />
          <label htmlFor="captions" className="text-sm text-gray-700">Has Captions</label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Duration (seconds)</label>
          <input
            type="number"
            value={metadata.duration_seconds || ''}
            onChange={(e) => onChange({ ...metadata, duration_seconds: parseInt(e.target.value) })}
            className="w-20 px-2 py-1 border rounded"
          />
        </div>
      </div>
    </div>
  );
}

// SEO Content metadata form
function SEOContentMetadataForm({ metadata, onChange }: { metadata: any; onChange: (data: any) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Page Title *</label>
        <input
          type="text"
          value={metadata.page_title || ''}
          onChange={(e) => onChange({ ...metadata, page_title: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="SEO page title (50-60 characters)"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          {(metadata.page_title || '').length}/60 characters
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description *</label>
        <textarea
          value={metadata.meta_description || ''}
          onChange={(e) => onChange({ ...metadata, meta_description: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Meta description (150-160 characters)"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          {(metadata.meta_description || '').length}/160 characters
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug *</label>
          <input
            type="text"
            value={metadata.url_slug || ''}
            onChange={(e) => onChange({ ...metadata, url_slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="page-url-slug"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target Keyword *</label>
          <input
            type="text"
            value={metadata.target_keyword || ''}
            onChange={(e) => onChange({ ...metadata, target_keyword: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
      </div>
    </div>
  );
}

// Main Asset Creation Component
export function AssetCreation() {
  const [assetType, setAssetType] = useState<AssetType>('email');
  const [file, setFile] = useState<File | null>(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [metadata, setMetadata] = useState<AssetMetadata>({
    type: 'email',
    owner: 'current-user', // This should come from auth context
    tags: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // HTML-based asset types
  const htmlAssetTypes = ['email', 'seo_content', 'faq', 'site_content'];
  const isHtmlAsset = htmlAssetTypes.includes(assetType);

  // File-based asset types
  const fileAssetTypes = ['image', 'video', 'podcast'];
  const isFileAsset = fileAssetTypes.includes(assetType);

  const handleAssetTypeChange = (type: AssetType) => {
    setAssetType(type);
    setMetadata({ ...metadata, type });
    setFile(null);
    setHtmlContent('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);

      // Extract basic metadata from file
      if (assetType === 'image') {
        const img = new Image();
        img.onload = () => {
          setMetadata({
            ...metadata,
            dimensions: { width: img.width, height: img.height }
          });
        };
        img.src = URL.createObjectURL(selectedFile);
      }
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFile = file;

      // For HTML-based assets, create HTML file from editor content
      if (isHtmlAsset) {
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        finalFile = new File([htmlBlob], `${assetType}_${Date.now()}.html`, { type: 'text/html' });
      }

      if (!finalFile) {
        alert('Please provide a file or content');
        setIsSubmitting(false);
        return;
      }

      // Prepare form data
      const formData = new FormData();
      formData.append('file', finalFile);
      formData.append('metadata', JSON.stringify({
        ...metadata,
        type: assetType,
      }));

      // Submit to API
      const response = await axios.post('/api/assets', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        alert('Asset created successfully!');
        // Reset form or redirect
        window.location.href = `/assets/${response.data.asset_id}`;
      }
    } catch (error) {
      console.error('Failed to create asset:', error);
      alert('Failed to create asset. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="border-b px-6 py-4">
          <h1 className="text-2xl font-bold">Create New Marketing Asset</h1>
          <p className="text-gray-600 mt-1">Choose an asset type and provide the required information</p>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center justify-between max-w-2xl">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300'
              }`}>
                1
              </div>
              <span className="ml-2 text-sm font-medium">Asset Type</span>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <div className={`flex items-center ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300'
              }`}>
                2
              </div>
              <span className="ml-2 text-sm font-medium">Metadata</span>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <div className={`flex items-center ${currentStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300'
              }`}>
                3
              </div>
              <span className="ml-2 text-sm font-medium">Content</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Asset Type Selection */}
          {currentStep === 1 && (
            <>
              <AssetTypeSelector value={assetType} onChange={handleAssetTypeChange} />
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Next: Metadata
                </button>
              </div>
            </>
          )}

          {/* Step 2: Metadata */}
          {currentStep === 2 && (
            <>
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-4">Asset Information</h2>

                {/* Common metadata fields */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={metadata.title || ''}
                      onChange={(e) => setMetadata({ ...metadata, title: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Asset title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={metadata.description || ''}
                      onChange={(e) => setMetadata({ ...metadata, description: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      placeholder="Brief description"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Campaign</label>
                      <input
                        type="text"
                        value={metadata.campaign || ''}
                        onChange={(e) => setMetadata({ ...metadata, campaign: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Campaign name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                      <input
                        type="text"
                        value={metadata.target_audience || ''}
                        onChange={(e) => setMetadata({ ...metadata, target_audience: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., B2B, Millennials"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                    <input
                      type="text"
                      value={metadata.tags.join(', ')}
                      onChange={(e) => setMetadata({
                        ...metadata,
                        tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                      })}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Comma-separated tags"
                    />
                  </div>
                </div>

                {/* Type-specific metadata forms */}
                <div className="border-t pt-6">
                  <h3 className="text-md font-semibold mb-4">
                    {assetType.charAt(0).toUpperCase() + assetType.slice(1).replace('_', ' ')} Specific Information
                  </h3>

                  {assetType === 'email' && (
                    <EmailMetadataForm metadata={metadata} onChange={setMetadata} />
                  )}
                  {assetType === 'image' && (
                    <ImageMetadataForm metadata={metadata} onChange={setMetadata} />
                  )}
                  {assetType === 'video' && (
                    <VideoMetadataForm metadata={metadata} onChange={setMetadata} />
                  )}
                  {assetType === 'seo_content' && (
                    <SEOContentMetadataForm metadata={metadata} onChange={setMetadata} />
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Next: Content
                </button>
              </div>
            </>
          )}

          {/* Step 3: Content */}
          {currentStep === 3 && (
            <>
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-4">Asset Content</h2>

                {/* HTML-based assets use TipTap editor */}
                {isHtmlAsset && (
                  <div>
                    <TipTapEditor
                      content={htmlContent}
                      onChange={setHtmlContent}
                      assetType={assetType as any}
                    />
                  </div>
                )}

                {/* File-based assets use file upload */}
                {isFileAsset && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                    <div className="text-center">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <span className="text-blue-600 hover:text-blue-700 font-medium">
                          Click to upload
                        </span>
                        <span className="text-gray-500"> or drag and drop</span>
                      </label>
                      <input
                        id="file-upload"
                        type="file"
                        onChange={handleFileChange}
                        className="hidden"
                        accept={
                          assetType === 'image' ? 'image/*' :
                          assetType === 'video' ? 'video/*' :
                          assetType === 'podcast' ? 'audio/*' :
                          '*'
                        }
                      />
                      {file && (
                        <div className="mt-4 p-3 bg-gray-50 rounded inline-block">
                          <p className="text-sm text-gray-700">
                            {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Ad assets need special handling */}
                {assetType === 'ad' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ad Platform</label>
                      <select
                        value={metadata.platform || ''}
                        onChange={(e) => setMetadata({ ...metadata, platform: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select platform...</option>
                        <option value="google_ads">Google Ads</option>
                        <option value="facebook">Facebook/Instagram</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="twitter">Twitter/X</option>
                        <option value="tiktok">TikTok</option>
                        <option value="display">Display Network</option>
                      </select>
                    </div>

                    <TipTapEditor
                      content={htmlContent}
                      onChange={setHtmlContent}
                      assetType="site_content"
                      placeholder="Enter ad copy..."
                    />

                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-2">Upload creative assets (optional)</p>
                      <input
                        type="file"
                        onChange={handleFileChange}
                        multiple
                        accept="image/*,video/*"
                        className="text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Create Asset
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AssetCreation;